import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { ArrowLeft, Truck, Printer, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const statusColors = {
  unfulfilled: '#dc2626', processing: '#ca8a04', shipped: '#2563eb',
  in_transit: '#6366f1', delivered: '#16a34a', refunded: '#6b7280', confirmed: '#16a34a', assigned: '#8b5cf6',
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);

  const [newStatus, setNewStatus] = useState('');
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('');
  const [note, setNote] = useState('');
  const [updating, setUpdating] = useState(false);

  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  const [internalNote, setInternalNote] = useState('');

  useEffect(() => {
    api.get(`/orders/${id}`).then(r => {
      setOrder(r.data);
      setLoading(false);
      setRefundAmount(r.data.total?.toFixed(2));
    }).catch(() => { setLoading(false); toast.error('Order not found'); });
    // Fetch suppliers for assignment
    api.get('/admin/stats').then(() => {}).catch(() => {});
  }, [id]);

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setUpdating(true);
    try {
      const payload = { status: newStatus, note };
      if (tracking) payload.tracking_number = tracking;
      if (carrier) payload.carrier = carrier;
      const { data } = await api.put(`/orders/${id}/status`, payload);
      setOrder(data);
      toast.success('Status updated');
      setNewStatus(''); setNote('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setUpdating(false);
  };

  const handleRefund = async () => {
    try {
      const { data } = await api.post(`/orders/${id}/refund`, { amount: parseFloat(refundAmount), reason: refundReason });
      setOrder(data);
      setRefundOpen(false);
      toast.success('Refund processed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    }
  };

  const handleAddNote = async () => {
    if (!internalNote.trim()) return;
    try {
      const { data } = await api.post(`/orders/${id}/notes`, { note: internalNote });
      setOrder(data);
      setInternalNote('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w || !order) return;
    const items = order.items?.map(i => `<tr><td style="padding:8px;border-bottom:1px solid #eee">${i.title}${i.variant_title ? ` (${i.variant_title})` : ''}</td><td style="padding:8px;border-bottom:1px solid #eee;text-align:right">${i.quantity}</td></tr>`).join('');
    const addr = order.shipping_address || {};
    w.document.write(`<!DOCTYPE html><html><head><title>Packing Slip #${order.order_number}</title><style>body{font-family:Inter,sans-serif;padding:40px;max-width:600px;margin:0 auto}h1{font-size:20px;margin-bottom:4px}table{width:100%;border-collapse:collapse;margin-top:20px}th{text-align:left;padding:8px;border-bottom:2px solid #333;font-size:12px;text-transform:uppercase}</style></head><body><h1>Packing Slip</h1><p style="color:#666;font-size:13px">Order #${order.order_number} - ${new Date(order.created_at).toLocaleDateString()}</p><p style="margin-top:20px"><strong>Ship To:</strong><br>${order.customer_name}<br>${addr.line1 || ''}${addr.line2 ? ', ' + addr.line2 : ''}<br>${addr.city || ''}, ${addr.state || ''} ${addr.zip || ''}</p><table><thead><tr><th>Item</th><th style="text-align:right">Qty</th></tr></thead><tbody>${items}</tbody></table><script>window.print()</script></body></html>`);
    w.document.close();
  };

  if (loading) return <div className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</div>;
  if (!order) return <div className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Order not found</div>;

  const inputStyle = { borderColor: 'rgba(26,26,26,0.12)' };
  const labelStyle = { color: 'rgba(26,26,26,0.6)' };

  return (
    <motion.div data-testid="order-detail-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center gap-4 mb-8">
        <button data-testid="back-to-orders" onClick={() => navigate('/admin/orders')} className="p-2 rounded-lg hover:bg-[rgba(26,26,26,0.05)]">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-2xl tracking-tight" style={{ color: '#1A1A1A' }}>Order #{order.order_number}</h1>
            {order.is_test && <Badge style={{ backgroundColor: '#ca8a04', color: '#fff', border: 'none' }} className="text-[10px] uppercase rounded-sm">Test</Badge>}
          </div>
          <p className="font-sans text-sm mt-0.5" style={{ color: 'rgba(26,26,26,0.5)' }}>{new Date(order.created_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="print-packing-slip" variant="outline" onClick={handlePrint} className="rounded-none font-sans text-xs gap-1" style={inputStyle}>
            <Printer size={13} /> Packing Slip
          </Button>
          {order.payment_status !== 'refunded' && (
            <Button data-testid="refund-btn" variant="outline" onClick={() => setRefundOpen(true)} className="rounded-none font-sans text-xs gap-1 text-red-600" style={inputStyle}>
              <AlertTriangle size={13} /> Refund
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Items</h3>
            <div className="space-y-3">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-12 flex-shrink-0 overflow-hidden rounded" style={{ backgroundColor: 'rgba(26,26,26,0.03)' }}>
                    {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-sans text-sm" style={{ color: '#1A1A1A' }}>
                      {item.title}
                      {item.is_upsell && <span className="ml-1 px-1.5 py-0.5 text-[9px] uppercase rounded" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>Upsell</span>}
                    </p>
                    {item.variant_title && <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.45)' }}>{item.variant_title}</p>}
                  </div>
                  <span className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>x{item.quantity}</span>
                  <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>${(item.unit_price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t mt-4 pt-3 space-y-1" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
              <div className="flex justify-between font-sans text-sm"><span style={{ color: 'rgba(26,26,26,0.5)' }}>Subtotal</span><span>${order.subtotal?.toFixed(2)}</span></div>
              {order.bundle_discount > 0 && <div className="flex justify-between font-sans text-sm"><span style={{ color: '#16a34a' }}>Bundle</span><span style={{ color: '#16a34a' }}>-${order.bundle_discount?.toFixed(2)}</span></div>}
              {order.coupon_discount > 0 && <div className="flex justify-between font-sans text-sm"><span style={{ color: '#16a34a' }}>Coupon ({order.coupon_code})</span><span style={{ color: '#16a34a' }}>-${order.coupon_discount?.toFixed(2)}</span></div>}
              <div className="flex justify-between font-sans text-sm"><span style={{ color: 'rgba(26,26,26,0.5)' }}>Shipping</span><span>{order.shipping === 0 ? 'Free' : `$${order.shipping?.toFixed(2)}`}</span></div>
              <div className="flex justify-between pt-2 border-t font-sans font-medium" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                <span>Total</span><span className="text-lg">${order.total?.toFixed(2)}</span>
              </div>
              {order.refund_amount > 0 && (
                <div className="flex justify-between font-sans text-sm"><span className="text-red-600">Refunded</span><span className="text-red-600">-${order.refund_amount?.toFixed(2)}</span></div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Timeline</h3>
            <div className="space-y-3">
              {[...(order.status_history || [])].reverse().map((entry, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full mt-1" style={{ backgroundColor: statusColors[entry.status] || '#6b7280' }} />
                    {i < (order.status_history?.length || 0) - 1 && <div className="w-px flex-1 mt-1" style={{ backgroundColor: 'rgba(26,26,26,0.08)' }} />}
                  </div>
                  <div className="pb-2">
                    <p className="font-sans text-sm font-medium capitalize" style={{ color: '#1A1A1A' }}>{entry.status?.replace(/_/g, ' ')}</p>
                    {entry.note && <p className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.5)' }}>{entry.note}</p>}
                    <p className="font-sans text-[10px]" style={{ color: 'rgba(26,26,26,0.3)' }}>{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Update Status */}
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Update Fulfillment</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="update-status-select" className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="in_transit">In Transit</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Carrier</Label>
                <Select value={carrier} onValueChange={setCarrier}>
                  <SelectTrigger className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USPS">USPS</SelectItem>
                    <SelectItem value="UPS">UPS</SelectItem>
                    <SelectItem value="FedEx">FedEx</SelectItem>
                    <SelectItem value="DHL">DHL</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mb-3">
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Tracking Number</Label>
              <Input data-testid="tracking-input" value={tracking} onChange={e => setTracking(e.target.value)}
                placeholder="Enter tracking number" className="font-sans text-sm rounded-none" style={inputStyle} />
            </div>
            <div className="mb-3">
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Note</Label>
              <Textarea value={note} onChange={e => setNote(e.target.value)}
                placeholder="Optional note..." className="font-sans text-sm rounded-none min-h-[60px]" style={inputStyle} />
            </div>
            <Button data-testid="update-status-btn" onClick={handleUpdateStatus} disabled={!newStatus || updating}
              className="rounded-none font-sans text-sm" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
              Update Status
            </Button>
          </div>

          {/* Internal Notes */}
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Internal Notes</h3>
            {order.notes?.length > 0 && (
              <div className="space-y-2 mb-4">
                {order.notes.map((n, i) => (
                  <div key={i} className="p-3 rounded" style={{ backgroundColor: 'rgba(26,26,26,0.02)' }}>
                    <p className="font-sans text-sm">{n.text}</p>
                    <p className="font-sans text-[10px] mt-1" style={{ color: 'rgba(26,26,26,0.3)' }}>{new Date(n.timestamp).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Textarea data-testid="internal-note" value={internalNote} onChange={e => setInternalNote(e.target.value)}
                placeholder="Add a note..." className="font-sans text-sm rounded-none min-h-[40px] flex-1" style={inputStyle} />
              <Button data-testid="add-note-btn" onClick={handleAddNote} variant="outline" className="rounded-none font-sans text-sm self-end" style={inputStyle}>Add</Button>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Customer</h3>
            <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{order.customer_name || '—'}</p>
            <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{order.customer_email}</p>
            {order.customer_phone && <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>{order.customer_phone}</p>}
          </div>

          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Shipping Address</h3>
            {order.shipping_address?.line1 ? (
              <div className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.6)' }}>
                <p>{order.shipping_address.line1}</p>
                {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
                <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
                <p>{order.shipping_address.country}</p>
              </div>
            ) : <p className="font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>No address provided</p>}
          </div>

          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Payment</h3>
            <div className="space-y-2">
              <div className="flex justify-between"><span className="font-sans text-sm" style={labelStyle}>Status</span>
                <Badge className="rounded-sm font-sans text-[10px] uppercase"
                  style={{ backgroundColor: order.payment_status === 'paid' ? 'rgba(34,197,94,0.1)' : 'rgba(107,114,128,0.1)', color: order.payment_status === 'paid' ? '#16a34a' : '#6b7280', border: 'none' }}>
                  {order.payment_status}
                </Badge>
              </div>
              <div className="flex justify-between"><span className="font-sans text-sm" style={labelStyle}>Fulfillment</span>
                <Badge className="rounded-sm font-sans text-[10px] uppercase"
                  style={{ backgroundColor: (statusColors[order.fulfillment_status] || '#6b7280') + '1a', color: statusColors[order.fulfillment_status] || '#6b7280', border: 'none' }}>
                  {order.fulfillment_status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              {order.tracking_number && (
                <div className="flex justify-between"><span className="font-sans text-sm" style={labelStyle}>Tracking</span>
                  <span className="font-mono text-xs" style={{ color: '#1A1A1A' }}>{order.tracking_number}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Refund Dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif">Process Refund</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Refund Amount</Label>
              <Input data-testid="refund-amount" type="number" step="0.01" value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
                className="font-sans text-sm rounded-none" style={inputStyle} />
            </div>
            <div>
              <Label className="font-sans text-xs mb-1 block" style={labelStyle}>Reason</Label>
              <Textarea data-testid="refund-reason" value={refundReason} onChange={e => setRefundReason(e.target.value)}
                placeholder="Reason for refund..." className="font-sans text-sm rounded-none" style={inputStyle} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)} className="rounded-none font-sans text-sm">Cancel</Button>
            <Button data-testid="confirm-refund" onClick={handleRefund} className="rounded-none font-sans text-sm bg-red-600 text-white hover:bg-red-700">Process Refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
